/**
 * Adds functionality to tertiary dropdown elements
 *
 * Custom event is created for the element. Input value data is added to the event.
 *
 * Listening to the custom event and getting the data:
 * document.querySelector('.tertiary-dropdown').addEventListener('tertiaryChange', (event) => {
 *   console.log(event.data);
 * });
 */
class TertiaryDropdown {
  /**
   * Constructor. Starts everything up if tabs are found
   * @param {HTMLElement} element element to bind logic into
   */
  constructor(element) {
    this._element = element;
    this._handle = this._element.querySelector('.tertiary-dropdown__handle');
    this._dropdown = this._element.querySelector('.tertiary-dropdown__options-container');
    this._optionsList = this._element.querySelector('.tertiary-dropdown__options');
    this._inputs = Array.from( this._dropdown.querySelectorAll('input[type="radio"]') );
    this._labels = Array.from( this._dropdown.querySelectorAll('label') );

    this._bindElements();
  }

  /**
   * bindElements is called initially by constructor.
   */
  _bindElements() {
    this._element.addEventListener('click', (event) => {
      if (event.target.classList.contains('tertiary-dropdown__handle')) {
        this._toggleDropdown();
      } else if (event.target.tagName === 'LABEL') {
        this._inputLabelClick(event);
      }
    });

    // Close dropdown on click outside of the element
    document.documentElement.addEventListener('click', this._handleOutsideClick.bind(this));

    this._inputs.forEach((input) => {
      input.addEventListener('change', this.inputChange.bind(this));
    });
  }

  /**
   * Scroll dropdown list to selected option
   */
  _toggleDropdown() {
    const isActive = this._element.classList.toggle('active');

    if (isActive) {
      this._scrollSelectedIntoView();
    }
  }

  /**
   * Scroll dropdown list to selected option
   */
  _scrollSelectedIntoView() {
    const selectedOption = this._optionsList.querySelector('input[type="radio"]:checked');

    if (selectedOption) {
      const selectedOptionOffset = this._optionsList.clientHeight / 2;
      const selectedOptionPos = selectedOption.parentNode.offsetTop;

      this._optionsList.scrollTo({ top: selectedOptionPos - selectedOptionOffset });
    }
  }

  /**
   * Called on document click event
   * @param {Event} event click event object
   */
  _handleOutsideClick(event) {
    if (!event.target.closest('.tertiary-dropdown')) {
      this._element.classList.remove('active');
    }
  }

  /**
   * Called on label click event
   * @param {Event} event click event object
   */
  _inputLabelClick(event) {
    const selectedInput = this._inputs.find((input) => input.id === event.target.getAttribute('for'));
    if (selectedInput.checked) {
      event.preventDefault();
      selectedInput.checked = false;

      this.inputChange();
    }

    this._toggleDropdown();
  }

  /**
   * Called on input change event
   * @param {boolean} [fireEvent=true] if true, custom event is fired
   */
  inputChange(fireEvent = true) {
    this._updateDropdown(fireEvent);
  }

  /**
   * Update dropdown texts, classes and event data
   * @param {boolean} [fireEvent=true] if true, custom event is fired
   */
  _updateDropdown(fireEvent = true) {
    const selectedInput = this._inputs.find((input) => input.checked);
    let dropdownData = { value: '' };

    if (selectedInput) {
      const selectedInputLabel = this._labels.find((label) => label.getAttribute('for') === selectedInput.id);
      this._handle.innerText = selectedInputLabel.innerText;
      this._element.classList.add('selected');

      dropdownData.value = selectedInput.value;
    } else {
      this._handle.innerText = this._handle.dataset.emptyvalue ?? 'Valitse';
      this._element.classList.remove('selected');
    }


    if(fireEvent) {
      const tertiaryChangeEvent = new CustomEvent('tertiaryChange', { detail: dropdownData });
      this._element.dispatchEvent(tertiaryChangeEvent);
    }
  }

  /**
   * init Static initializer of the class.
   */
  static init() {
    const tertiaryDropdowns = document.querySelectorAll('.tertiary-dropdown');
    window.tertiaries = {};
    tertiaryDropdowns.forEach((element) => {
      const tertiary = new TertiaryDropdown(element);
      if(element.id) {
        window.tertiaries[element.id] = tertiary;
      }
    });
  }
}

export default TertiaryDropdown;
